import { useState, useEffect } from 'react';
import API from '@/lib/api';
import { KpiCard } from '@/components/KpiCard';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Battery, Eye, Boxes, Warehouse, Truck, AlertTriangle, Package, Bell, ShieldCheck
} from 'lucide-react';
import { toast } from 'sonner';

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [warehouses, setWarehouses] = useState([]);
  const [alertas, setAlertas] = useState([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState('all');
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      const params = {};
      if (selectedWarehouse !== 'all') params.warehouse_id = selectedWarehouse;
      const [dashRes, whRes, alertRes] = await Promise.all([
        API.get('/inventario/dashboard', { params }),
        API.get('/catalogs/warehouses'),
        API.get('/reportes/alertas')
      ]);
      setData(dashRes.data); setWarehouses(whRes.data); setAlertas(alertRes.data);
    } catch (err) { toast.error('Error cargando dashboard'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [selectedWarehouse]);

  if (loading) return <div className="flex items-center justify-center h-64"><p className="text-muted-foreground">Cargando...</p></div>;

  const kpis = data?.kpis || {};
  const groupSummary = data?.group_summary || [];
  const specialWh = data?.special_warehouses || {};

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-[#151A22]">Panel de Control</h1>
          <p className="text-sm text-muted-foreground">Resumen de inventario en tiempo real</p>
        </div>
        <Select value={selectedWarehouse} onValueChange={setSelectedWarehouse}>
          <SelectTrigger className="w-[220px]" data-testid="topbar-warehouse-switcher"><SelectValue placeholder="Almacén" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los almacenes</SelectItem>
            {warehouses.map(w => (<SelectItem key={w.id} value={w.id}>{w.nombre}</SelectItem>))}
          </SelectContent>
        </Select>
      </div>

      {/* KPI Cards - Updated model */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
        <KpiCard title="Usados Disponibles" value={kpis.usado_disponible} icon={Battery} />
        <KpiCard title="En Revisión" value={kpis.usado_en_revision} icon={Eye} />
        <KpiCard title="Pendientes Revisión" value={kpis.pendientes_revision} icon={AlertTriangle} className={kpis.pendientes_revision > 0 ? 'border-amber-300' : ''} />
        <KpiCard title="En Tarima" value={kpis.en_tarima} icon={Package} />
        <KpiCard title="Salidas Garantía" value={kpis.salidas_garantia} icon={ShieldCheck} />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard title="Tarimas Incompletas" value={kpis.tarimas_incompletas} icon={Boxes} />
        <KpiCard title="Tarimas Completas" value={kpis.tarimas_completas} icon={Boxes} />
        <KpiCard title="En Bodega" value={kpis.tarimas_en_bodega} icon={Warehouse} />
        <KpiCard title="Salidas Hoy" value={kpis.tarimas_salida_hoy} icon={Truck} />
      </div>

      {/* Special Warehouses */}
      {Object.keys(specialWh).length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {Object.entries(specialWh).map(([key, wh]) => (
            <Card key={key} className="bg-white border">
              <CardContent className="p-4 flex items-center justify-between">
                <div><p className="text-xs text-muted-foreground">{wh.nombre}</p><p className="text-xl font-semibold mt-1">{wh.total_piezas} pzas</p></div>
                <div className="h-10 w-10 rounded-lg bg-amber-100 flex items-center justify-center"><Warehouse className="h-5 w-5 text-amber-600" /></div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Alerts */}
      {alertas.length > 0 && (
        <Card className="bg-white border border-amber-200">
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Bell className="h-4 w-4 text-amber-500" /> Alertas ({alertas.length})</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2">
              {alertas.map((a, i) => (
                <div key={i} className={`p-2.5 rounded-lg border text-sm flex items-start gap-2 ${a.severidad === 'warning' ? 'bg-amber-50 border-amber-200 text-amber-800' : 'bg-blue-50 border-blue-200 text-blue-800'}`}>
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" /><span>{a.mensaje}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Group Summary Table - Updated columns */}
      <Card className="bg-white border">
        <CardHeader className="pb-3"><CardTitle className="text-base">Resumen por Grupo</CardTitle></CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/30">
                  <TableHead className="font-semibold">Grupo</TableHead>
                  <TableHead className="text-right">Cap. Tarima</TableHead>
                  <TableHead className="text-right">Disponibles</TableHead>
                  <TableHead className="text-right">En Revisión</TableHead>
                  <TableHead className="text-right">En Tarima</TableHead>
                  <TableHead className="text-right">T. Incompletas</TableHead>
                  <TableHead className="text-right">T. Completas</TableHead>
                  <TableHead className="text-right">En Bodega</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {groupSummary.map(row => (
                  <TableRow key={row.group_code} className="hover:bg-muted/40">
                    <TableCell className="font-medium">{row.group_code}</TableCell>
                    <TableCell className="text-right">{row.capacidad_tarima}</TableCell>
                    <TableCell className="text-right font-semibold">{row.usado_disponible || 0}</TableCell>
                    <TableCell className="text-right">{row.usado_en_revision || 0}</TableCell>
                    <TableCell className="text-right">{row.en_tarima || 0}</TableCell>
                    <TableCell className="text-right">{row.tarimas_incompleta || 0}</TableCell>
                    <TableCell className="text-right">{row.tarimas_completa || 0}</TableCell>
                    <TableCell className="text-right">{row.tarimas_en_bodega || 0}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
